'use client';

/* ──────────────────────────────────────────────
 *  FormField — Generic form field renderer
 *  Supports text, textarea, select, number, checkbox, date
 * ────────────────────────────────────────────── */

import { useCallback } from 'react';
import type { CustomFieldType } from '../../types';
import { openNativeDatePicker } from '../../utils/native-picker';

interface FormFieldProps {
  id: string;
  label: string;
  type: CustomFieldType;
  value: string | number | boolean;
  onChange: (value: string | number | boolean) => void;
  required?: boolean;
  placeholder?: string;
  options?: string[];
  min?: number;
  max?: number;
  error?: string;
  onBlur?: () => void;
}

export function FormField({
  id,
  label,
  type,
  value,
  onChange,
  required,
  placeholder,
  options,
  min,
  max,
  error,
  onBlur,
}: FormFieldProps) {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
      if (type === 'checkbox') {
        onChange((e.target as HTMLInputElement).checked);
      } else if (type === 'number') {
        onChange(e.target.value === '' ? '' : Number(e.target.value));
      } else {
        onChange(e.target.value);
      }
    },
    [onChange, type],
  );

  if (type === 'checkbox') {
    return (
      <div className="bt-field bt-field--checkbox">
        <label className="bt-field__checkbox-label" htmlFor={id}>
          <input
            id={id}
            type="checkbox"
            className="bt-field__checkbox"
            checked={!!value}
            onChange={handleChange}
            onBlur={onBlur}
          />
          <span>
            {label}
            {required && <span className="bt-field__required"> *</span>}
          </span>
        </label>
        {error && <span className="bt-field__error">{error}</span>}
      </div>
    );
  }

  return (
    <div className="bt-field">
      <label className="bt-field__label" htmlFor={id}>
        {label}
        {required && <span className="bt-field__required"> *</span>}
      </label>

      {type === 'textarea' ? (
        <textarea
          id={id}
          className={`bt-field__input bt-field__input--textarea ${error ? 'bt-field__input--error' : ''}`}
          value={String(value ?? '')}
          onChange={handleChange}
          onBlur={onBlur}
          placeholder={placeholder}
          required={required}
          rows={3}
        />
      ) : type === 'select' ? (
        <select
          id={id}
          className={`bt-field__input bt-field__input--select ${error ? 'bt-field__input--error' : ''}`}
          value={String(value ?? '')}
          onChange={handleChange}
          onBlur={onBlur}
          required={required}
        >
          <option value="">{placeholder ?? '—'}</option>
          {options?.map(
            (opt): React.ReactNode => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ),
          )}
        </select>
      ) : (
        <input
          id={id}
          type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
          className={`bt-field__input ${error ? 'bt-field__input--error' : ''}`}
          value={type === 'number' ? (value === '' ? '' : Number(value)) : String(value ?? '')}
          onChange={handleChange}
          onBlur={onBlur}
          onFocus={type === 'date' ? openNativeDatePicker : undefined}
          onClick={type === 'date' ? openNativeDatePicker : undefined}
          placeholder={placeholder}
          required={required}
          min={min}
          max={max}
        />
      )}

      {error && <span className="bt-field__error">{error}</span>}
    </div>
  );
}
